 //AccessControl.js
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, Edit2, Trash2, UserPlus, Eye, Clock, Shield, MapPin, ChevronDown, ChevronRight} from 'lucide-react';
import { useToast } from '../../generic_components/Toast/Toast';
import {
  getBusinesses,
  getUsers,
  inviteUser,
  updateUser,
  removeUser,
  getSites,
  getUserStats,
  formatUserForAPI,
  getErrorMessage,
  filterUsers
} from './accessControlUtils';
import InvitationManagement from '../invite/InvitationManagement';
import HolographicSecurityLoader from './AccessSpinner';
import './AccessControl.css'; // Import the external CSS file

import SideBarSmallScreen from '../SideBarSmallScreen';
import { url } from '../../api/endpointURL';
import { webURL } from '../../config';

// Import generic components
import Select from '../../generic_components/Select/Select';
import Input from '../../generic_components/Input/Input';
import Table from '../../generic_components/Table/Table';
import Button from '../../generic_components/Button/Button';
import Modal from '../../generic_components/Modal/Modal';
import Footer from '../../generic_components/Footer/Footer';
import Card from '../../generic_components/Card/Card';


const AccessControl = () => {  
  const isSmallScreen = window.innerWidth < 600;

  // State management
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [currentBusinessRole, setCurrentBusinessRole] = useState(null);
  const [siteData, setSiteData] = useState({
  groups: [],
  ungroupedSites: [],
  allSites: []
});

  // SEPARATE DATA LOADING FROM ANIMATION DISPLAY
  const [dataLoading, setDataLoading] = useState(true);     // Tracks actual data loading
  const [showAnimation, setShowAnimation] = useState(true); // Controls animation display
  
  const [error, setError] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');

  // Add invitation tab state
  const [invitationTabOpen, setInvitationTabOpen] = useState(false);

  // Add window width state for responsive design
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const toast = useToast();

  // Add focus state for glassmorphism inputs
  // const [focusedInput, setFocusedInput] = useState(null);
  
  // CONSOLIDATED RBAC STATE - Single source of truth
  const [rbacData, setRbacData] = useState({
    globalRole: '',
    businessRoles: [],
    isLoaded: false
  });

  const fetchAndStoreRBAC = async (email) => {
    try {
      const url_path = url+'/fetch_rbac'; // Use environment variable or default to localhost
      const response = await fetch(url_path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user: email })
      });
      
      const rbacResponse = await response.json();
      // console.log("RBAC fetched:", rbacResponse);

      // Store in localStorage for persistence
      localStorage.setItem('rbac_role', rbacResponse.global_role);
      localStorage.setItem('rbac_data', JSON.stringify(rbacResponse.business_roles));
      
      // Update state - single source of truth
      setRbacData({
        globalRole: rbacResponse.global_role,
        businessRoles: rbacResponse.business_roles || [],
        isLoaded: true
      });
      
      return rbacResponse;
    } catch (error) {
      console.error('Error fetching RBAC data:', error);
      // Fallback to localStorage if fetch fails
      const storedRole = localStorage.getItem('rbac_role') || '';
      let storedBusinessRoles = [];
      try {
        storedBusinessRoles = JSON.parse(localStorage.getItem('rbac_data')) || [];
      } catch {
        storedBusinessRoles = [];
      }
      
      setRbacData({
        globalRole: storedRole,
        businessRoles: storedBusinessRoles,
        isLoaded: true
      });
    }
  };

  // Use rbacData state instead of localStorage
  const role = rbacData.globalRole?.toLowerCase();
  const rbacBusinessRoles = rbacData.businessRoles;

  const filteredBusinesses = useMemo(() => {
    if (!businesses || businesses.length === 0 || !rbacData.isLoaded) return [];
    
    // If TLA, show all businesses
    if (role === 'tla') {
      return businesses;
    }
    
    // Otherwise, filter based on rbac_data
    if (!Array.isArray(rbacBusinessRoles) || rbacBusinessRoles.length === 0) {
      console.warn('No valid rbac_data found');
      return [];
    }
    
    // Get businesses where user has admin or supervisor role
    // Technicians shouldn't see businesses in Access Control
    const userBusinessesWithAccess = rbacBusinessRoles
      .filter(item => {
        const itemRole = item.role?.toLowerCase();
        return itemRole === 'admin' || itemRole === 'supervisor';
      })
      .map(item => item.business);
    
    return businesses.filter(business => 
      userBusinessesWithAccess.includes(business.name) || 
      userBusinessesWithAccess.includes(business.code) || 
      userBusinessesWithAccess.includes(business.id)
    );
  }, [businesses, role, rbacBusinessRoles, rbacData.isLoaded]);

  const getUserRoleForBusiness = useCallback((businessId) => {
    if (!businessId || !rbacData.isLoaded) return null;
    
    // If TLA, they have admin access to all businesses
    if (role === 'tla') return 'admin';
    
    // Find the user's role for this specific business
    const businessData = rbacBusinessRoles.find(item => {
      const business = businesses.find(b => b.id === businessId);
      return business && (
        item.business === business.name || 
        item.business === business.code || 
        item.business === business.id
      );
    });
    
    return businessData ? businessData.role.toLowerCase() : null;
  }, [businesses, rbacBusinessRoles, rbacData.isLoaded, role]);  

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'technician',
    selectedSites: [],
    inviter_email: '',
  });

  const roles = [
    { value: 'admin', label: 'Admin', description: 'Full access to all sites and user management' },
    { value: 'supervisor', label: 'Supervisor', description: 'Read-only access to all sites and user viewing' },
    { value: 'technician', label: 'Technician', description: 'Site-specific access for maintenance tasks' }
  ];

  // SINGLE useEffect for initialization
  useEffect(() => {
    const email = localStorage.getItem('email');
    
    if (!email) {
      window.location.href = '/#auth/signin';
      return;
    }

    const initializeData = async () => {
      try {
        setDataLoading(true);
        setError(null);
        
        // Fetch RBAC data first
        await fetchAndStoreRBAC(email);
        
        // Then get available businesses
        const businessesData = await getBusinesses();
        
        // Remove duplicates based on ID
        const uniqueBusinesses = businessesData.filter((business, index, self) => 
          index === self.findIndex(b => b.id === business.id)
        );
        
        setBusinesses(uniqueBusinesses);
        
      } catch (error) {
        console.error('Error loading initial data:', error);
        setError(getErrorMessage(error));
      } finally {
        setDataLoading(false);
      }
    };

    initializeData();
  }, []); // Empty dependency array - run once only

  // Auto-select business when filtered businesses are available
  useEffect(() => {
    if (filteredBusinesses.length > 0 && !selectedBusiness && rbacData.isLoaded) {
      setSelectedBusiness(filteredBusinesses[0].id);
    } else if (filteredBusinesses.length === 0 && businesses.length > 0 && rbacData.isLoaded) {
      // User doesn't have access to any businesses with proper role
      setError('You don\'t have admin or supervisor access to any businesses.');
    }
  }, [filteredBusinesses, selectedBusiness, businesses.length, rbacData.isLoaded]);

  //  const loadBusinessData = useCallback(async () => {
  //   if (!selectedBusiness) return;
    
  //   try {
  //     setDataLoading(true);  // Use dataLoading
  //     setError(null);
      
  //     // Load all data for the selected business in parallel
  //     const [usersData, sitesData, statsData] = await Promise.all([
  //       getUsers(selectedBusiness),
  //       getSites(selectedBusiness),
  //       getUserStats(selectedBusiness)
  //     ]);
      
  //     setUsers(usersData);
  //     setSites(sitesData);
  //     setStats(statsData);

  //     const adjustedStats = {
  //       ...statsData,
  //       totalUsers: usersData.filter(user => user.role.toLowerCase() !== 'tla').length
  //     };
  //     setStats(adjustedStats);
      
  //   } catch (error) {
  //     console.error('Error loading business data:', error);
  //     setError(getErrorMessage(error));
  //   } finally {
  //     setDataLoading(false);  // Data loading complete
  //   }
  // }, [selectedBusiness]);

  const loadBusinessData = useCallback(async () => {
  if (!selectedBusiness) return;
  
  try {
    setDataLoading(true);
    setError(null);
    
    // Load all data for the selected business in parallel
    const [usersData, sitesData, statsData] = await Promise.all([
      getUsers(selectedBusiness),
      getSites(selectedBusiness),
      getUserStats(selectedBusiness)
    ]);
    
    setUsers(usersData);
    setSiteData(sitesData); // Now stores the grouped structure
    setStats(statsData);

    const adjustedStats = {
      ...statsData,
      totalUsers: usersData.filter(user => user.role.toLowerCase() !== 'tla').length
    };
    setStats(adjustedStats);
    
  } catch (error) {
    console.error('Error loading business data:', error);
    setError(getErrorMessage(error));
  } finally {
    setDataLoading(false);
  }
}, [selectedBusiness]);

const handleGroupToggle = (groupName, sitesInGroup) => {
  const allSelected = sitesInGroup.every(site => 
    formData.selectedSites.includes(site)
  );
  
  if (allSelected) {
    // Deselect all sites in group
    setFormData(prev => ({
      ...prev,
      selectedSites: prev.selectedSites.filter(
        site => !sitesInGroup.includes(site)
      )
    }));
  } else {
    // Select all sites in group
    const newSites = [...new Set([...formData.selectedSites, ...sitesInGroup])];
    setFormData(prev => ({
      ...prev,
      selectedSites: newSites
    }));
  }
};

const isGroupFullySelected = (sitesInGroup) => {
  return sitesInGroup.every(site => formData.selectedSites.includes(site));
};

  // Load data when business selection changes
  useEffect(() => {
    if (selectedBusiness !== null) {
      loadBusinessData();
    }
  }, [selectedBusiness, loadBusinessData]);

  // Update current business role when selection changes
  useEffect(() => {    
    if (selectedBusiness && rbacData.isLoaded) {
      const businessRole = getUserRoleForBusiness(selectedBusiness);
      setCurrentBusinessRole(businessRole);
    }
  }, [selectedBusiness, businesses, rbacData, getUserRoleForBusiness]);

  // Auto-close invitation tab when no pending invitations
  useEffect(() => {
    if (stats.pendingInvitations === 0) {
      setInvitationTabOpen(false);
    }
  }, [stats.pendingInvitations]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleBusinessChange = (businessId) => {
    setSelectedBusiness(businessId);
  };

  // Filter users based on search and role filter
  const filteredUsers = filterUsers(users, searchTerm, filterRole);

  const formatLastActive = (dateString) => {
    const date = new Date(dateString.replace(/Z$/, "")); // Get rid of ending 'Z' since stored signup time is actually in local timezone not UTC
    const now = new Date();

    // Extract year, month, day in local time
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Difference in milliseconds
    const diffTime = nowOnly - dateOnly;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    // console.log(`Line 312 tthe date is ${date} and now is ${now} `)
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const handleOpenModal = (type, user = null) => {
    setModalType(type);
    setSelectedUser(user);
    setShowModal(true);
    setError(null);
    
    if (type === 'edit' && user) {
      setFormData({
        name: user.name,
        email: user.email,
        role: user.role,
        selectedSites: user.sites.includes('all') ? [] : user.sites,
        inviter_email: localStorage.getItem('email')
      });
    } else if (type === 'invite') {
      setFormData({
        name: '',
        email: '',
        role: 'technician',
        selectedSites: [],
        inviter_email: localStorage.getItem('email')
      });
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setFormData({ name: '', email: '', role: 'technician', selectedSites: [], inviter_email: localStorage.getItem('email') });
    setError(null);
    // setFocusedInput(null); // Reset focus state
  };

  const handleSiteToggle = (site) => {
    setFormData(prev => ({
      ...prev,
      selectedSites: prev.selectedSites.includes(site)
        ? prev.selectedSites.filter(s => s !== site)
        : [...prev.selectedSites, site]
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);
      setError(null);
      
      const userData = formatUserForAPI(formData);
      
      if (modalType === 'invite') {
        const newInvitation = await inviteUser(userData, selectedBusiness);
        console.log('Invitation created:', newInvitation);
        const successMsgInvite = `Invitation sent to ${formData.email}`;
        toast.success(successMsgInvite, {
          autoClose: 5000
        });
  
      } else if (modalType === 'edit') {
        const updatedUser = await updateUser(selectedUser.id, userData, selectedBusiness);
        setUsers(prev => prev.map(user => 
          user.id === selectedUser.id ? updatedUser : user
        ));
        const successMsgUpdate = `User updated successfully`;
        toast.success(successMsgUpdate, {
          autoClose: 5000
        });
      }
      
      // Refresh stats
      const statsData = await getUserStats(selectedBusiness);
      setStats(statsData);
      
      handleCloseModal();
    } catch (error) {
      console.error('Error submitting form:', error);
      setError(getErrorMessage(error));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleRemoveUser = async () => {
    try {
      setSubmitLoading(true);
      setError(null);
      
      await removeUser(selectedUser.id, selectedBusiness);
      setUsers(prev => prev.filter(user => user.id !== selectedUser.id));
      
      // Refresh stats
      const statsData = await getUserStats(selectedBusiness);
      setStats(statsData);
      
      handleCloseModal();
    } catch (error) {
      console.error('Error removing user:', error);
      setError(getErrorMessage(error));
    } finally {
      setSubmitLoading(false);
    }
  };

  const getRoleClassName = (role) => {
    switch (role) {
      case 'tla': return 'role-tla';
      case 'admin': return 'role-admin';
      case 'supervisor': return 'role-supervisor';
      case 'technician': return 'role-technician';
      default: return 'role-default';
    }
  };

  const getStatusClassName = (status) => {
    // console.log('getStatusClassName called with status:', status);
    if (status === 'active') {
      return 'status-active';
    } else if (status === 'Never Active') {
      return 'status-neveractive';
    } else {
      return 'status-inactive';
    }
  };

  // Responsive style helpers
  const isMobile = windowWidth <= 640;
  const isSmallMobile = windowWidth <= 480;

  const filteredBusinessesOptions = filteredBusinesses.map((business, index) => (
    {
      label: business.name,
      value: business.id
    }
  ));

  const roleOptions = roles.map((role, index) => (
    {
      label:`${role.label} - ${role.description}`,
      value: role.value
    }
  ))

  // Access control table loading columns
  const access_control_table_loading_columns = [
    { key: 'user', label: 'User' },
    { key: 'role', label: 'Role' },
    { key: 'sitesAccess', label: 'Sites Access' },
    { key: 'dateJoined', label: 'Date Joined' },
    { key: 'status', label: 'Status' },
    { key: 'actions', label: 'Actions' }
  ];

  // Access control table loading data
  const access_control_table_loading_data = [1, 2, 3, 4, 5].map(index => ( 
    {
      user: (
        <div>
          <div className="user-name loading-skeleton">Loading User Name</div>
          <div className="user-email loading-skeleton email">loading@example.com</div>
        </div>
      ),
      role: (
        <span className="role-badge loading-skeleton">Loading Role</span>
      ),
      sitesAccess: (
        <div className="site-info">
          <MapPin style={{width: '16px', height: '16px', color: '#4a5568'}} />
          <span className="site-text loading-skeleton">Loading sites</span>
        </div>
      ),
      dateJoined: (
        <div className="site-info">
          <Clock style={{width: '16px', height: '16px', color: '#4a5568'}} />
          <span className="site-text loading-skeleton">Loading time</span>
        </div>
      ),
      status: (
        <span className="loading-skeleton">Loading</span>
      ),
      actions: (
        <div className="actions-container">
          <button className="action-button" style={{opacity: 0.3}} disabled>
            <Eye style={{width: '16px', height: '16px'}} />
          </button>
          <button className="action-button" style={{opacity: 0.3}} disabled>
            <Edit2 style={{width: '16px', height: '16px'}} />
          </button>
          <button className="action-button" style={{opacity: 0.3}} disabled>
            <Trash2 style={{width: '16px', height: '16px'}} />
          </button>
        </div>
      )
    }
  ));


  // Show animation until BOTH data is loaded AND animation is complete
  if (showAnimation || dataLoading || !rbacData.isLoaded) {
    return (
      <>
        {/* Holographic Security Loader */}
        <HolographicSecurityLoader 
          onComplete={() => setShowAnimation(false)}
          duration={1000} // 1 second animation - you can adjust this
        />
        
        {/* Content Below Spinner - Blurred and Dimmed */}
        <div style={{
          filter: 'blur(8px)',
          opacity: 0.3,
          pointerEvents: 'none',
          position: 'relative',
          zIndex: 1
        }}>
          {/* <div className={`access-control-container ${isMobile ? 'mobile' : sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}> */}
          <div className={`access-control-container${isMobile ? ' mobile': ''}`}>
            <div className="access-control-max-width">
              {/* Header */}
              <div className="access-control-header">
                <h1 className="access-control-title">User Management</h1>
                <p className="access-control-subtitle">Manage user access and site permissions</p>
                
                {/* Business Selector - Only show if multiple businesses */}
                {filteredBusinesses.length > 1 && (
                  <div className="business-selector">
                    <Select
                      value={selectedBusiness || ''}
                      onChange={(e) => handleBusinessChange(e.target.value)}
                      options={filteredBusinessesOptions}
                      size={isSmallScreen ? 'sm' : 'md'}
                      selectStyle={{width: isSmallScreen ? '12rem' : '16rem'}}
                    />
                  </div>
                )}
              </div>

              {/* Error Display */}
              {error && (
                <div className="error-card">
                  {error}
                </div>
              )}

              {/* Controls */}
              {/* <div className="glass-card-access-control"> */}
              <Card className="glass-card no-shadow no-hover" style={{marginBottom: '1rem', padding: '15px'}}>
                <div className={`controls-container ${isMobile ? 'mobile' : ''}`}>
                  {/* Row 1: Search */}                  
                  <div className="access-control-search-container">
                    <Input
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Search className="search-icon" />
                  </div>
                 

                  {/* Row 2: Role Filter */}
                  <div >
                    <Select
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                      options={[
                        {label: 'All Roles', value: 'all'},
                        {label: 'Admin', value: 'admin'},
                        {label: 'Supervisor', value: 'supervisor'},
                        {label: 'Technician', value: 'technician'},
                      ]}
                    />
                  </div>

                  {/* Row 3: Invite Button */}
                  {(currentBusinessRole === 'admin' || role === 'tla') && (
                    <div>
                      <button
                        className="invite-button"
                        onClick={() => handleOpenModal('invite')}
                      >
                        <UserPlus style={{ width: '16px', height: '16px' }} />
                        Invite User
                      </button>
                    </div>
                  )}
                </div>
              </Card>
              {/* </div> */}

              {/* Loading skeleton for table and stats */}
              <div className="table-container">
                <Table 
                  columns = {access_control_table_loading_columns}
                  data = {access_control_table_loading_data}
                  thStyle={{fontSize: isSmallScreen ? '12px' : '16px'}}
                  tdStyle={{fontSize: isSmallScreen ? '10px' : '14px'}}
                />
              </div>

              {/* Stats - Loading State */}

              <div className="stats-grid">
                {/* <div className="stat-card"> */}
                <Card className="glass-card no-shadow no-hover">
                  <div className="stat-content">
                    <div>
                      <p className="stat-label">Total Users</p>
                      <p className="stat-value loading-skeleton">Loading</p>
                    </div>
                    <div className="stat-icon users">
                      <MapPin style={{width: '16px', height: '16px', color: '#c4b5fd'}} />
                    </div>
                  </div>
                {/* </div> */}
                </Card>
                
                {/* <div className="stat-card"> */}
                <Card className="glass-card no-shadow no-hover">
                  <div className="stat-content">
                    <div>
                      <p className="stat-label">Pending Invitations</p>
                      <p className="stat-value loading-skeleton">Loading</p>
                    </div>
                    <div className="stat-icon invitations">
                      <Clock style={{width: '16px', height: '16px', color: '#fbbf24'}} />
                    </div>
                  </div>
                {/* </div> */}
                </Card>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const access_control_table_columns = [
    { key: 'user', label: 'User' },
    { key: 'role', label: 'Role' },
    { key: 'sitesAccess', label: 'Sites Access' },
    { key: 'dateJoined', label: 'Date Joined' },
    { key: 'status', label: 'Status' },
    // Conditionally include Actions column
    ...(currentBusinessRole === 'admin' || role === 'tla'
      ? [{ key: 'actions', label: 'Actions' }]
      : []),
  ];

  const access_control_table_data = filteredUsers
  .filter(user => user.role.toLowerCase() !== 'tla')
  .map((user) => (
    {
      user: (
        <div>
          <div className="user-name">{user.name}</div>
          <div className="user-email">{user.email}</div>
        </div>
      ),
      role: (
        <span className={`role-badge ${getRoleClassName(user.role)}`}>
          <Shield style={{width: '12px', height: '12px'}} />
          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
        </span>
      ),
      sitesAccess: (
        <div className="site-info">
          <MapPin style={{width: '16px', height: '16px', color: '#d1d5db'}} />
          <span className="site-text">
            {user.sites.includes('all') ? 
              (user.role === 'supervisor' ? 'All Sites (Read-only)' : 
              user.role === 'tla' ? 'All Sites (TLA Access)' : 'All Sites') : 
              `${user.sites.length} site${user.sites.length !== 1 ? 's' : ''}`}
          </span>
        </div>
      ),
      dateJoined: (
        <div className="site-info">
          <Clock style={{width: '16px', height: '16px', color: '#d1d5db'}} />
          <span className="site-text">
            {formatLastActive(user.last_active)}
          </span>
        </div>
      ),
      status: (
        <span className={`${getStatusClassName(user.status)}`} style={{fontWeight: '500'}}>
          {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
        </span>
      ),
      // (currentBusinessRole === 'admin' || role === 'tla') && (
        actions: (
          <div className="actions-container">
            <button
              className="action-button view"
              onClick={() => handleOpenModal('view', user)}
              title="View Details"
            >
              <Eye style={{width: '16px', height: '16px'}} />
            </button>
            {(currentBusinessRole === 'admin' || role === 'tla') && (
              <>
                <button
                  className="action-button edit"
                  onClick={() => handleOpenModal('edit', user)}
                  title="Edit User"
                >
                  <Edit2 style={{width: '16px', height: '16px'}} />
                </button>
                <button
                  className="action-button delete"
                  onClick={() => handleOpenModal('remove', user)}
                  title="Remove Access"
                >
                  <Trash2 style={{width: '16px', height: '16px'}} />
                </button>
              </>
            )}
            {currentBusinessRole === 'supervisor' && (
              <span className="view-only-text">View Only</span>
            )}
          </div>
        )
      // )
    }
  ));

  return (
    <>
      {/* Side Bar */}
      {/* {!isSmallScreen && <div style={{ marginTop: '-3.3em' }}>
        <SideBarTest />
        </div>
      } */}
      {/* <div className={`access-control-container ${isMobile ? 'mobile' : sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}> */}
      <div className={`access-control-container${isMobile ? ' mobile': ''}`}>
        <div className="access-control-max-width">
          {/* Header */}
          <div className="access-control-header">
            <h1 className="access-control-title">User Management</h1>
            <p className="access-control-subtitle">Manage user access and site permissions</p>
            
            {/* Business Selector - Only show if multiple businesses */}
            {filteredBusinesses.length > 0 && (
              <div className="business-selector">
                <Select
                  value={selectedBusiness || ''}
                  onChange={(e) => handleBusinessChange(e.target.value)}
                  options={filteredBusinessesOptions}
                  size={isSmallScreen ? 'sm' : 'md'}
                  selectStyle={{width: isSmallScreen ? '12rem' : '16rem'}}
                />
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="error-card">
              {error}
            </div>
          )}

          {/* Controls */}
          {/* <div className="glass-card-access-control"> */}
          <Card className="glass-card no-shadow no-hover" style={{marginBottom: '1rem', padding: '15px'}}>
            <div className={`controls-container ${isMobile ? 'mobile' : ''}`}>
              <div className="controls-row">
                {/* Search */}
                <div className="access-control-search-container">
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search className="search-icon" />
                </div>

                {/* Role Filter */}
                <Select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  options={[
                    {label: 'All Roles', value: 'all'},
                    {label: 'Admin', value: 'admin'},
                    {label: 'Supervisor', value: 'supervisor'},
                    {label: 'Technician', value: 'technician'},
                  ]}
                />
              </div>
              
              {/* Invite User Button */}
              {(currentBusinessRole === 'admin' || role === 'tla') && (
                <button
                  className="invite-button"
                  onClick={() => handleOpenModal('invite')}
                >
                  <UserPlus style={{width: '16px', height: '16px'}} />
                  Invite User
                </button>
              )}
            </div>
          </Card>
          {/* </div> */}

          {/* Invitation Management Tab */}
          {(currentBusinessRole === 'admin' || role === 'tla') && stats.pendingInvitations > 0 && (
            // <div className="invitation-tab-container">
            <Card className="glass-card no-hover no-shadow" style={{padding: '0', marginBottom: '1rem'}}>
              <div 
                className={`invitation-tab-header ${invitationTabOpen ? 'open' : ''}`}
                onClick={() => setInvitationTabOpen(!invitationTabOpen)}
              >
                <div className="invitation-tab-title">
                  {invitationTabOpen ? (
                    <ChevronDown style={{width: '20px', height: '20px'}} />
                  ) : (
                    <ChevronRight style={{width: '20px', height: '20px'}} />
                  )}
                  <span>Pending Invitations</span>
                  <span className="invitation-tab-badge">
                    {stats.pendingInvitations}
                  </span>
                </div>
              </div>
              <div className={`invitation-tab-content ${invitationTabOpen ? 'open' : ''}`}>
                <InvitationManagement selectedBusiness={selectedBusiness} />
              </div>
            {/* </div> */}
            </Card>
          )}

          {/* Users Table */}
          <div className="access-control-table-container">
            <Table 
              columns = {access_control_table_columns}
              data = {access_control_table_data}
              tableWrapperStyle={{maxHeight: '450px'}}
              thStyle={{fontSize: isSmallScreen ? '12px' : '16px'}}
              tdStyle={{fontSize: isSmallScreen ? '10px' : '14px'}}
            />
          </div>

          {/* Stats */}
            <div className="stats-grid">
              {/* <div className="stat-card"> */}
              <Card className="glass-card no-shadow no-hover">
                <div className="stat-content">
                  <div>
                    <p className="stat-label">Total Users</p>
                    <p className="stat-value">{stats.totalUsers || 0}</p>
                  </div>
                  <div className="stat-icon users">
                    <MapPin style={{width: '16px', height: '16px', color: '#c4b5fd'}} />
                  </div>
                </div>
              </Card>
              {/* </div> */}
              
              {stats.pendingInvitations > 0 && (
                // <div className="stat-card">
                <Card className="glass-card no-shadow no-hover">
                  <div className="stat-content">
                    <div>
                      <p className="stat-label">Pending Invitations</p>
                      <p className="stat-value">{stats.pendingInvitations || 0}</p>
                    </div>
                    <div className="stat-icon invitations">
                      <Clock style={{width: '16px', height: '16px', color: '#fbbf24'}} />
                    </div>
                  </div>
                {/* </div> */}
                </Card>
              )}
            </div>
        </div>

        {/* Modal with completely separate CSS classes */}
        <Modal size='xl' show={showModal} onHide={handleCloseModal} position='center'>
            <Modal.Header >
              <Modal.Title>
                  {modalType === 'invite' && 'Invite New User'}
                  {modalType === 'edit' && 'Edit User'}
                  {modalType === 'view' && 'User Details'}
                  {modalType === 'remove' && 'Remove User Access'}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <div className={`access-control-modal-content ${isMobile ? 'mobile' : ''}`}>
                {/* Error Display in Modal */}
                {error && (
                  <div className="error-card">
                    {error}
                  </div>
                )}

                {(modalType === 'invite' || modalType === 'edit') && (
                  <div className="form-container">
                    <div className={`form-grid ${isSmallMobile ? 'small-mobile' : ''}`}>
                      <div className="form-group">
                        <Input
                          label="Full Name"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Enter full name"
                        />
                      </div>
                      <div className="form-group">
                        <Input
                          label="Email Address"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="Enter email address"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <Select
                        label="Role"
                        value={formData.role}
                        onChange={(e) => setFormData({...formData, role: e.target.value})}
                        options={roleOptions}
                      />
                    </div>
              
          {(formData.role !== 'admin' && formData.role !== 'supervisor' && formData.role !== 'tla') && (
            <div className="form-group">
              <label className="form-label">
                Site Access
              </label>
              <div className={`site-selection ${isMobile ? 'mobile' : ''}`}>
                
                {/* Render grouped sites section */}
                {siteData.groups.length > 0 && (
                  <div className="grouped-sites-section">
                    <div className="section-header">
                      <span className="header-icon">📁</span>
                      Grouped Sites
                    </div>
                    {siteData.groups.map(group => (
                      <div key={group.groupName} className={`site-group-item ${isGroupFullySelected(group.sites) ? 'selected' : ''}`}>
                        <label className="checkbox-label group-checkbox">
                          <input
                            type="checkbox"
                            className="checkbox group-check"
                            checked={isGroupFullySelected(group.sites)}
                            onChange={() => handleGroupToggle(group.groupName, group.sites)}                          
                          />
                          <span className="group-name-text">
                           <span>
                             <span className="group-icon">
                              {isGroupFullySelected(group.sites) ? '📂' : '📁'}
                            </span>
                            <span className="group-name">{group.groupName}</span>
                           </span>
                            <span className="site-count">{group.sites.length} site{group.sites.length !== 1 ? 's' : ''}</span>
                          </span>
                        </label>
                        
                        {/* Display sites in group with tree structure */}
                        <div className="group-sites-tree">
                          {group.sites.map((site, idx) => (
                            <div key={site} className="tree-item">
                              <div className="tree-line">
                                <span className="tree-connector">
                                  {idx === group.sites.length - 1 ? '└─' : '├─'}
                                </span>
                                <span className="tree-icon">🏢</span>
                                <span className="tree-site-name">{site}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Render all sites section */}
                {siteData.allSites.length > 0 && (
                  <div className="ungrouped-sites-section">
                    <div className="section-header">
                      <span className="header-icon">📄</span>
                      Individual Sites
                    </div>
                    <div className="ungrouped-sites-grid">
                      {siteData.allSites.map(site => (
                        <label key={site} className={`checkbox-label individual-site ${formData.selectedSites.includes(site) ? 'selected' : ''}`}>
                          <input
                            type="checkbox"
                            className="checkbox"
                            checked={formData.selectedSites.includes(site)}
                            onChange={() => handleSiteToggle(site)}
                          />
                          <span className="site-icon">🏢</span>
                          <span className="checkbox-text">{site}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
      
        {/* Show message if no sites available */}
        {siteData.groups.length === 0 && siteData.ungroupedSites.length === 0 && (
          <p className="info-text">No sites available for this business.</p>
        )}
        </div>       
      </div>
    )}

                    <div className={`button-group ${isMobile ? 'mobile' : ''}`}>
                      <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitLoading}
                                                                
                      >
                        {submitLoading ? 'Processing...' : (modalType === 'invite' ? 'Send Invitation' : 'Update User')}
                      </Button>
                      <Button
                        variant='ghost'
                        type="button"
                        onClick={handleCloseModal}
                        // className={`cancel-button ${isMobile ? 'mobile' : ''}`}
                        disabled={submitLoading}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {modalType === 'view' && selectedUser && (
                  <div className="form-container">
                    <div className={`view-details-grid ${isMobile ? 'mobile' : ''}`}>
                      <div className="details-section">
                        <h3 className="section-title">User Information</h3>
                        <div className="detail-item">
                          <span className="detail-label">Name:</span> <span className="detail-value">{selectedUser.name}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Email:</span> <span className="detail-value">{selectedUser.email}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Role:</span>
                          <span className={`role-badge ${getRoleClassName(selectedUser.role)}`} style={{marginLeft: '8px'}}>
                            {selectedUser.role.charAt(0).toUpperCase() + selectedUser.role.slice(1)}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Status:</span>
                          <span className={`${getStatusClassName(selectedUser.status)}`} style={{marginLeft: '8px'}}>
                            {selectedUser.status.charAt(0).toUpperCase() + selectedUser.status.slice(1)}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Date Joined:</span> <span className="detail-value">{formatLastActive(selectedUser.last_active)}</span>
                        </div>
                      </div>
                      <div className="details-section">
                        <h3 className="section-title">Site Access</h3>
                        {selectedUser.sites.includes('all') ? (
                          <div style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem'}}>
                            <p style={{
                              fontWeight: '500', 
                              color: selectedUser.role === 'supervisor' ? '#87ceeb' : 
                                     selectedUser.role === 'tla' ? '#7c3aed' : '#059669',
                              margin: '0'
                            }}>
                              All Sites {selectedUser.role === 'supervisor' ? '(Read-only Access)' : 
                                        selectedUser.role === 'tla' ? '(TLA Access)' : '(Full Access)'}
                            </p>
                          </div>
                        ) : (
                          <div className="sites-container">
                            {selectedUser.sites.length > 0 ? (
                              selectedUser.sites.map(site => (
                                <div key={site} className="site-item">
                                  <MapPin style={{width: '12px', height: '12px', color: '#9ca3af'}} />
                                  <span style={{fontSize: '14px', color: 'white'}}>{site}</span>
                                </div>
                              ))
                            ) : (
                              <p style={{color: '#f59e0b', margin: '0'}}>No sites assigned</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={`button-group ${isMobile ? 'mobile' : ''}`}>
                      {currentBusinessRole === 'admin' && (
                        <Button
                          onClick={() => handleOpenModal('edit', selectedUser)}
                          // className={`submit-button ${isMobile ? 'mobile' : ''}`}
                        >
                          Edit User
                        </Button>
                      )}
                      <Button
                        variant='ghost'
                        onClick={handleCloseModal}
                        // className={`cancel-button ${isMobile ? 'mobile' : ''}`}
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                )}

                {modalType === 'remove' && selectedUser && (
                  <div className="remove-confirmation">
                    <div className="remove-icon">
                      <Trash2 style={{height: '24px', width: '24px', color: '#dc2626'}} />
                    </div>
                    <h3 className="remove-title">Remove User Access</h3>
                    <p className="remove-text">
                      Are you sure you want to remove access for <strong>{selectedUser.name}</strong>? 
                      This action cannot be undone and they will lose access to all assigned sites.
                    </p>
                    <div className={`remove-button-group ${isMobile ? 'mobile' : ''}`}>
                      <Button
                        onClick={handleRemoveUser}
                        // className={`remove-button ${isMobile ? 'mobile' : ''}`}
                        variant='danger'
                        disabled={submitLoading}
                      >
                        {submitLoading ? 'Removing...' : 'Remove Access'}
                      </Button>

                      <Button
                        onClick={handleCloseModal}
                        // className={`cancel-button ${isMobile ? 'mobile' : ''}`}
                        variant='secondary'
                        disabled={submitLoading}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Modal.Body>
            {/* </div> */}
          {/* </div> */}
        {/* )} */}
        </Modal>

        {/* Loading Overlay for Submit Actions */}
        {submitLoading && (
          <div className="loading-overlay">
            <div>Processing request...</div>
          </div>
        )}
      </div>

      {isSmallScreen ? (
      <SideBarSmallScreen/>
      ) : null
      }

      <div className='home-footer-container'><Footer url={webURL}/></div>
    </>
  );
};

export default AccessControl;